{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://flashback.tech/schemas/dashboard/v1",
  "title": "Flashback Dashboard Spec v1",
  "description": "The durable, data-free, PII-free Dashboard Spec authored by the BI agent's compose_dashboard tool. Each panel carries a binding (a parameterized query against a semantic model) and a viz (how to draw the result). Data is transient; this spec is forever. Flat and recursion-free by construction so it is safe as a structured-output target and as a validation target (Visual-Dashboards design D1=B / D2=C).",
  "type": "object",
  "additionalProperties": false,
  "required": ["schema", "title", "scope", "layout", "panels"],
  "properties": {
    "schema": {
      "description": "Spec major-version discriminator. The validator binds to the major it embeds; forward/back compatibility is handled by upgraders-on-read, not by lax schemas (O7).",
      "const": "flashback.dashboard/v1"
    },
    "title": { "type": "string", "minLength": 1, "maxLength": 200 },
    "description": { "type": "string", "maxLength": 1000 },
    "scope": {
      "type": "object",
      "additionalProperties": false,
      "required": ["repoId"],
      "properties": {
        "repoId": { "type": "string", "minLength": 1 },
        "defaultModelId": {
          "description": "Model used by any panel binding that omits modelId. Panels may bind different models (O3); a single panel is one model (no cross-model join).",
          "type": "string"
        }
      }
    },
    "parameters": {
      "description": "Dashboard-level interactive controls. binding.filters reference these by id, so one control re-filters every panel and re-population just re-reads them. Locked cross-filter-ready now; rung-2 (global controls) ships early.",
      "type": "array",
      "maxItems": 24,
      "items": { "$ref": "#/$defs/parameter" }
    },
    "layout": {
      "description": "react-grid-layout-shaped, 12-column grid. Persisted verbatim; user-editable. Every item.i must match a panels[].id (enforced by the Go integrity check, not the schema).",
      "type": "array",
      "minItems": 1,
      "maxItems": 64,
      "items": { "$ref": "#/$defs/layoutItem" }
    },
    "panels": {
      "type": "array",
      "minItems": 1,
      "maxItems": 64,
      "items": { "$ref": "#/$defs/panel" }
    }
  },

  "$defs": {
    "measureRef": {
      "description": "A measure reference, e.g. \"[Efficiency %]\". The bracketed measure name; existence is B3's catalog bind-check, not structural.",
      "type": "string",
      "pattern": "^\\[.+\\]$"
    },
    "fieldRef": {
      "description": "A field/dimension reference in DAX style, e.g. \"Mechanic[MechanicId]\". Entity = entity localKey (table name); Column = field column name. The synthetic \"__label\" (filled engine-side by B4) is also a legal viz field but is NOT a binding fieldRef.",
      "type": "string",
      "pattern": "^.+\\[.+\\]$"
    },

    "parameter": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "type", "label"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "type": { "enum": ["daterange", "dimension", "scalar"] },
        "label": { "type": "string", "minLength": 1 },
        "field": { "$ref": "#/$defs/fieldRef" },
        "multi": { "type": "boolean" },
        "default": {
          "description": "Default control value. Shape depends on type: daterange => {relative} or {from,to}; dimension => null | string | array; scalar => a literal. Left unconstrained on purpose."
        }
      },
      "allOf": [
        {
          "if": { "properties": { "type": { "const": "dimension" } }, "required": ["type"] },
          "then": { "required": ["field"] }
        }
      ]
    },

    "layoutItem": {
      "type": "object",
      "additionalProperties": false,
      "required": ["i", "x", "y", "w", "h"],
      "properties": {
        "i": { "type": "string", "minLength": 1 },
        "x": { "type": "integer", "minimum": 0, "maximum": 11 },
        "y": { "type": "integer", "minimum": 0 },
        "w": { "type": "integer", "minimum": 1, "maximum": 12 },
        "h": { "type": "integer", "minimum": 1, "maximum": 64 }
      }
    },

    "filter": {
      "oneOf": [
        {
          "type": "object",
          "additionalProperties": false,
          "required": ["param"],
          "properties": { "param": { "type": "string", "minLength": 1 } }
        },
        {
          "type": "object",
          "additionalProperties": false,
          "required": ["field", "op", "value"],
          "properties": {
            "field": { "$ref": "#/$defs/fieldRef" },
            "op": { "enum": ["eq", "in", "gt", "gte", "lt", "lte", "between"] },
            "value": {
              "description": "Literal filter value. NOTE: a literal filter on a PII field is rejected by B4/B3 bind-check, not structurally."
            }
          }
        }
      ]
    },

    "topN": {
      "type": "object",
      "additionalProperties": false,
      "required": ["by", "n", "dir"],
      "properties": {
        "by": { "$ref": "#/$defs/measureRef" },
        "n": { "type": "integer", "minimum": 1, "maximum": 10000 },
        "dir": { "enum": ["asc", "desc"] }
      }
    },

    "binding": {
      "description": "The durable, data-free, PII-free query. Shape only — DAX compilation (B2) and catalog bind-check (B3: existence, grain in validGrains, cardinality != 'empty', PII labelField) are downstream.",
      "type": "object",
      "additionalProperties": false,
      "required": ["measures", "grain"],
      "properties": {
        "modelId": { "type": "string" },
        "measures": { "type": "array", "minItems": 1, "items": { "$ref": "#/$defs/measureRef" } },
        "dimensions": { "type": "array", "items": { "$ref": "#/$defs/fieldRef" } },
        "filters": { "type": "array", "items": { "$ref": "#/$defs/filter" } },
        "grain": {
          "description": "The grouping grain: an entity localKey or the literal \"total\". B3 checks grain is in the measure's validGrains (itself a list of localKeys + \"total\"). Grain is validated ONCE at authoring and trusted on every refresh (the runtime verifier ignores grain).",
          "type": "string",
          "minLength": 1
        },
        "topN": { "$ref": "#/$defs/topN" }
      }
    },

    "display": {
      "description": "The reserved last-mile PII re-hydration hook. B4 implements the two-query trick: it fills the synthetic __label column from labelField after the LLM is out of the loop. piiTier (a rehydration strategy) is distinct from B0's piiClass (a field classification).",
      "type": "object",
      "additionalProperties": false,
      "required": ["labelField"],
      "properties": {
        "labelField": { "$ref": "#/$defs/fieldRef" },
        "piiTier": { "enum": ["none", "rehydrate"] }
      }
    },

    "colorScale": {
      "description": "Semantic color contract (D6): the spec picks a role, never a raw hex or vendor palette. B6's theme module maps each role to an indigo-derived palette from the theme tokens (light + dark).",
      "type": "object",
      "additionalProperties": false,
      "required": ["scheme"],
      "properties": {
        "scheme": {
          "type": "object",
          "additionalProperties": false,
          "required": ["role"],
          "properties": { "role": { "enum": ["sequential", "categorical", "diverging", "goodBad"] } }
        },
        "domainMid": { "type": "number" }
      }
    },

    "channel": {
      "type": "object",
      "additionalProperties": false,
      "required": ["field", "type"],
      "properties": {
        "field": {
          "description": "A measureRef, a fieldRef, or the synthetic \"__label\".",
          "type": "string",
          "minLength": 1
        },
        "type": { "enum": ["nominal", "quantitative", "temporal", "ordinal"] },
        "aggregate": { "enum": ["sum", "mean", "min", "max", "count", "distinct"] },
        "sort": {
          "anyOf": [
            { "enum": ["-x", "x", "-y", "y", "ascending", "descending"] },
            { "type": "null" }
          ]
        },
        "scale": { "$ref": "#/$defs/colorScale" },
        "axis": {
          "type": "object",
          "additionalProperties": false,
          "properties": {
            "title": { "type": "string" },
            "format": { "type": "string" }
          }
        },
        "format": { "type": "string" }
      }
    },

    "encoding": {
      "description": "Flat, fixed channel set. No layer/concat/facet/repeat (multi-view is the envelope's job via panels[]+layout[]) and no transform/condition/expression (anything computed is precomputed by the binding). This flatness is what removes Vega-Lite's recursion.",
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "x": { "$ref": "#/$defs/channel" },
        "y": { "$ref": "#/$defs/channel" },
        "color": { "$ref": "#/$defs/channel" },
        "size": { "$ref": "#/$defs/channel" },
        "theta": { "$ref": "#/$defs/channel" },
        "row": { "$ref": "#/$defs/channel" },
        "column": { "$ref": "#/$defs/channel" }
      }
    },

    "chartViz": {
      "description": "The curated, recursion-stripped Vega-Lite subset for chart panels.",
      "type": "object",
      "additionalProperties": false,
      "required": ["mark", "encoding"],
      "properties": {
        "mark": { "enum": ["bar", "line", "area", "point", "arc", "rule", "tick", "rect"] },
        "encoding": { "$ref": "#/$defs/encoding" }
      }
    },

    "kpiViz": {
      "type": "object",
      "additionalProperties": false,
      "required": ["format"],
      "properties": {
        "format": { "type": "string", "minLength": 1 },
        "delta": {
          "type": "object",
          "additionalProperties": false,
          "required": ["vs"],
          "properties": { "vs": { "enum": ["previous_period", "prior_year", "none"] } }
        },
        "spark": { "type": "boolean" }
      }
    },

    "gaugeViz": {
      "type": "object",
      "additionalProperties": false,
      "required": ["format", "min", "max"],
      "properties": {
        "format": { "type": "string", "minLength": 1 },
        "min": { "type": "number" },
        "max": { "type": "number" },
        "thresholds": {
          "type": "array",
          "items": {
            "type": "object",
            "additionalProperties": false,
            "required": ["value", "role"],
            "properties": {
              "value": { "type": "number" },
              "role": { "enum": ["sequential", "categorical", "diverging", "goodBad"] }
            }
          }
        }
      }
    },

    "tableViz": {
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "columns": {
          "type": "array",
          "items": {
            "type": "object",
            "additionalProperties": false,
            "required": ["field"],
            "properties": {
              "field": { "type": "string", "minLength": 1 },
              "label": { "type": "string" },
              "format": { "type": "string" },
              "conditional": { "type": "boolean" }
            }
          }
        },
        "sort": {
          "type": "object",
          "additionalProperties": false,
          "required": ["field", "dir"],
          "properties": {
            "field": { "type": "string", "minLength": 1 },
            "dir": { "enum": ["asc", "desc"] }
          }
        },
        "pageSize": { "type": "integer", "minimum": 1, "maximum": 1000 }
      }
    },

    "interaction": {
      "description": "Cross-filter reservation (interactivity rung 3). Reserved in the schema for forward-compat; the runtime (a click writes a param) is B6, not B1.",
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "emits": {
          "type": "array",
          "items": {
            "type": "object",
            "additionalProperties": false,
            "required": ["param", "from"],
            "properties": {
              "param": { "type": "string", "minLength": 1 },
              "from": { "enum": ["x", "y", "color", "size", "theta", "row", "column"] }
            }
          }
        }
      }
    },

    "panel": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "type", "title", "binding", "viz"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "type": { "enum": ["kpi", "gauge", "table", "bar", "line", "area", "point", "arc", "rule", "tick", "rect"] },
        "title": { "type": "string", "minLength": 1, "maxLength": 200 },
        "binding": { "$ref": "#/$defs/binding" },
        "display": { "$ref": "#/$defs/display" },
        "viz": { "type": "object" },
        "interaction": { "$ref": "#/$defs/interaction" }
      },
      "allOf": [
        {
          "if": { "properties": { "type": { "const": "kpi" } }, "required": ["type"] },
          "then": { "properties": { "viz": { "$ref": "#/$defs/kpiViz" } } }
        },
        {
          "if": { "properties": { "type": { "const": "gauge" } }, "required": ["type"] },
          "then": { "properties": { "viz": { "$ref": "#/$defs/gaugeViz" } } }
        },
        {
          "if": { "properties": { "type": { "const": "table" } }, "required": ["type"] },
          "then": { "properties": { "viz": { "$ref": "#/$defs/tableViz" } } }
        },
        {
          "if": {
            "properties": { "type": { "enum": ["bar", "line", "area", "point", "arc", "rule", "tick", "rect"] } },
            "required": ["type"]
          },
          "then": { "properties": { "viz": { "$ref": "#/$defs/chartViz" } } }
        }
      ]
    }
  }
}
